// Unified model for the "Подбор" section (merged Tender + Analytics).
// Each Selection has a "goal" that drives wizard steps and detail layout.
// Procurement and tender use the live Zakooper API; analytics goals stay on mocks.

import type { GpPivotPreset, GpPluginRank, GpPositionRow } from "./GroupPricing";

export type SelectionGoal =
  | "procurement" // закупка под склад / клиента (Zakooper live API)
  | "tender" // КП клиенту с гарантией цен (Zakooper live API + negotiation)
  | "other" // классическая упрощённая групповая проценка (group_search manual)
  | "competitors" // сверка с конкурентами (mock + pivot)
  | "monitoring" // мониторинг цен (mock + triggers)
  | "pricing" // ценообразование (mock + pivot)
  | "forecast"; // прогноз потребности (mock, нет API)

export type SelectionState =
  | "draft"
  | "pricing" // идёт проценка
  | "planning" // рекомендации готовы, ждём выбора
  | "negotiation" // отправлено клиенту
  | "purchasing" // запущены заказы поставщикам
  | "completed"
  | "cancelled"
  | "archived";

export interface SelectionDocOsn {
  uuid: string | null;
  title: string;
}

export interface SelectionStrategy {
  preset: "fast" | "balanced" | "max" | "custom";
  algos: number[];
  // Кодирует ОБА списка брендов сразу (контракт закупера/1С): доверенные — как
  // есть (`NARVA`), исключаемые — с префиксом `!` (`!MAHLE`). Разбор — splitBrands.
  // Содержимое обоих списков хранится всегда; свитчеры лишь включают/выключают
  // применение фильтра (на бэк уходит только включённая часть).
  brandsTrusted: string;
  brandsTrustedOn: boolean;
  brandsExcludeOn: boolean;
  maxTime: number; // Ручной подбор: начальный срок 1–40, 41 = любой; остальные цели — контракт движка.
  priceDiffPercent: number; // tender.price_diff_percent
  extraDays: number;
  orderType: 1 | 2;
  isAutoorder: 0 | 1;
}

/**
 * Форма создания/обновления согласования с клиентом — отправляется в
 * `POST /zakooper/purchases/<uuid>/negotiate` обёрнутая в `{ item: ... }`.
 *
 * info_type — bitmask: 1=email, 2=sms, 3=email+sms.
 */
export interface SelectionNegotiateForm {
  recommendation_uuids: string[];
  tm_percent: number;
  delivery_type: 0 | 1;
  phone: string;
  email: string;
  manager_name: string;
  manager_phone: string;
  manager_email: string;
  info_type: number;
  is_autostart: 0 | 1;
  manager_comment: string;
  is_allowed_orders: 0 | 1;
  ttl_days: number;
  prepay: number;
}

export function emptyNegotiateForm(): SelectionNegotiateForm {
  return {
    recommendation_uuids: [],
    tm_percent: 25,
    delivery_type: 1,
    phone: "",
    email: "",
    manager_name: "",
    manager_phone: "",
    manager_email: "",
    info_type: 1,
    is_autostart: 0,
    manager_comment: "",
    is_allowed_orders: 0,
    ttl_days: 14,
    prepay: 0,
  };
}

export interface SelectionNegotiationConfig {
  enabled: boolean;
  clientEmail: string;
  clientPhone: string;
  managerName: string;
  managerEmail: string;
  managerComment: string;
  tmPercent: number;
  ttlDays: number;
  prepay: number;
  isAutostart: 0 | 1;
  isAllowedOrders: 0 | 1;
}

export interface SelectionAnalyticsConfig {
  /**
   * Снимок выбранных plugin id на момент запуска (только для analytics). Берётся
   * из `state.plugins.map(p => p.id)` и сохраняется в run для отображения в
   * SelectionDetail. На бэке передаётся через `settings.plugins[]`, никаких
   * «свои поставщики vs конкуренты» серверный контракт не различает.
   */
  competitorPluginIds: number[];
  pivotPreset: GpPivotPreset;
  schedule: "once" | "daily" | "weekly" | "monthly";
  notifyEmail: string;
  notifyOnFinish: boolean;
  triggers: {
    id: string;
    type: "top_price" | "price_diff" | "min_offer_count" | "not_found";
    param?: number;
  }[];
}

export interface SelectionRun {
  uuid: string;
  /**
   * Live link к zakooper purchase. Заполнено только у procurement/tender.
   * У analytics-целей живёт через group_search (см. gsRequestId), у forecast — null.
   */
  purchaseUuid: string | null;
  /**
   * Live link к group_search. Заполнено у analytics-целей (competitors /
   * monitoring / pricing). У forecast — null. У procurement/tender — null
   * (gs_request_id сидит внутри ZakPurchaseDto.gs_request_id).
   */
  gsRequestId?: string | null;
  /** Клиентский GUID, отправляемый при create/update GS. Хранится локально. */
  gsClientId?: string | null;
  goal: SelectionGoal;
  title: string;
  state: SelectionState;
  docOsn: SelectionDocOsn | null;
  createdAt: string;
  updatedAt: string;
  deadlineAt: string | null;
  positionsTotal: number;
  positionsFinished: number;
  totalAmount: number | null;
  averageTime: number | null;
  pluginsCount: number;
  strategy: SelectionStrategy;
  negotiation?: SelectionNegotiationConfig;
  analytics?: SelectionAnalyticsConfig;
  // Lightweight cache of related zakooper data for UI
  algoDescriptions?: Record<string, string>;
  pinned?: boolean;
  /**
   * CAB-627: true, когда backend ещё не перевёл state покупки в completed, но
   * вкладка «Заказы поставщикам» видит, что оформление реально завершено (нет
   * ни одной in-flight позиции) — гасит точку-индикатор в сайдбаре, не дожидаясь
   * backend-статуса. Осмысленно только при state === "purchasing".
   */
  ordersSettled?: boolean;
}

export interface SelectionWizardState {
  goal: SelectionGoal | null;
  title: string;
  docOsn: SelectionDocOsn;
  /** Крайний срок закупки (ISO-дата YYYY-MM-DD) → purchase_deadline. null — не задан. */
  deadline: string | null;
  positions: GpPositionRow[];
  plugins: GpPluginRank[];
  strategy: SelectionStrategy;
  negotiation: SelectionNegotiationConfig;
  analytics: SelectionAnalyticsConfig;
}

export interface SelectionProcessLogEntry {
  id: string;
  runId: string;
  title: string;
  goal: SelectionGoal;
  state: SelectionState;
  progress: number;
  positionsFinished: number;
  positionsTotal: number;
  etaSeconds: number;
  startedAt: string;
}

export const ALGO_LABELS: Record<number, string> = {
  1: "Лучшая по цене · Запрошенный",
  2: "Лучший по сроку · Запрошенный",
  3: "Лучшая по цене · Доверенные аналоги",
  4: "Лучший по сроку · Доверенные аналоги",
  5: "Лучшая по цене · Все аналоги",
  6: "Лучший по сроку · Все аналоги",
  7: "Гибридный — ослабляем фильтры",
  8: "Без фильтров — лучшая по цене",
};

// Короткие мнемо-коды алгоритмов для узких шапок столбцов сводки. Схема:
// Л(учшая) + Ц(ена)/С(рок) + область поиска — ЗА (запрошенный артикул),
// ДА (доверенные аналоги), ВА (все аналоги). Полное имя и пояснение — в тултипе.
export const ALGO_SHORT: Record<number, string> = {
  1: "ЛЦЗА",
  2: "ЛСЗА",
  3: "ЛЦДА",
  4: "ЛСДА",
  5: "ЛЦВА",
  6: "ЛСВА",
  7: "ГИБ",
  8: "БФ",
};

// Пояснение алгоритма для тултипа шапки — что именно он оптимизирует и где ищет.
export const ALGO_DESCRIPTIONS: Record<number, string> = {
  1: "Минимальная цена строго по запрошенному артикулу, без аналогов.",
  2: "Минимальный срок поставки строго по запрошенному артикулу, без аналогов.",
  3: "Минимальная цена; разрешены доверенные аналоги.",
  4: "Минимальный срок; разрешены доверенные аналоги.",
  5: "Минимальная цена; разрешены любые аналоги.",
  6: "Минимальный срок; разрешены любые аналоги.",
  7: "Гибридный: постепенно ослабляет фильтры ради максимального покрытия потребностей.",
  8: "Без фильтров: абсолютный минимум цены по всем найденным предложениям.",
};

export const ALGO_REQUIRES_TRUSTED = new Set([3, 4, 7]);

/**
 * Набор способов отбора по умолчанию: запрошенный артикул и все аналоги, в
 * каждой области — и «дешевле», и «быстрее».
 *
 * Раньше здесь были пресеты «Сэкономить»/«Привезти быстрее»/«Побольше
 * вариантов», а внутри области выбирали между ценой и сроком. От выбора
 * отказались (CAB-489, просьба тех. директора): пользователю всё равно, как это
 * нагружает систему, и он всегда отмечал оба варианта ради большего числа
 * предложений — теперь оба включаются сами.
 */
export const DEFAULT_STRATEGY_ALGOS: number[] = [1, 2, 5, 6];
